import { defineComponent, defineNuxtPlugin } from '#imports'
import { h } from 'vue'
import { useStore } from 'vuex'
import type { NavigationFailure, RouteLocationRaw, Router } from 'vue-router'
import { RouterLink } from 'vue-router'
import { localizeRoute } from '@b2ag/utils/src/i18n-routing'

import { routerBeforeEach } from '@/app/router/beforeEach'
import { routerAfterEach } from '@/app/router/afterEach'

export default defineNuxtPlugin({
  name: 'router',
  enforce: 'pre',
  dependsOn: ['locale', 'auth service', 'store'],
  setup(nuxtApp) {
    const { $router }: { $router: Router } = nuxtApp

    // eslint-disable-next-line no-underscore-dangle, no-param-reassign
    delete nuxtApp.vueApp._context.components['RouterLink']

    nuxtApp.vueApp.component(
      'RouterLink',
      defineComponent({
        name: 'RouterLink',
        functional: true,
        props: {
          to: {
            type: [Object, String],
            required: true,
          },
          custom: Boolean,
          replace: Boolean,
          activeClass: String,
          exactActiveClass: String,
          ariaCurrentValue: String,
        },
        setup:
          (props, { slots }) =>
          () =>
            h(
              RouterLink,
              {
                ...props,
                to: localizeRoute(props.to, $router.currentRoute.value.path, $router.resolve),
              },
              slots,
            ),
      }),
    )

    const originalPush = $router.push
    const originalReplace = $router.replace

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    $router.push = async (to: RouteLocationRaw): Promise<NavigationFailure | void> => {
      const finalTo = localizeRoute(to, $router.currentRoute.value.path, $router.resolve)
      try {
        await originalPush.call($router, finalTo)
      } catch (err: any) {
        if (err.name !== 'NavigationDuplicated') {
          // eslint-disable-next-line no-console
          console.error(err)
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    $router.replace = async (to: RouteLocationRaw): Promise<NavigationFailure | void> => {
      const finalTo = localizeRoute(to, $router.currentRoute.value.path, $router.resolve)
      try {
        await originalReplace.call($router, finalTo)
      } catch (err: any) {
        if (err.name !== 'NavigationDuplicated') {
          // eslint-disable-next-line no-console
          console.error(err)
        }
      }
    }

    $router.beforeEach((to, from, next) => routerBeforeEach({ storeInstance: useStore() }, to, from, next))

    $router.afterEach((to, from) => routerAfterEach({ storeInstance: useStore() }, to, from))
  },
})
